import {
  CURRENT_SCHEMA_VERSION,
  type HouseConfig,
  type StoryConfig,
} from '@/model/schema';
import { resolveConstraints, type GenerationConstraints } from './constraints';
import { STYLE_PRESETS, type StylePresetSpec } from './stylePresets';
import { createRng, type Rng } from './rng';
import { generateLot } from './lotGenerator';
import { generateFootprint } from './footprintGenerator';
import { planStory } from './roomPlanner';
import { generateOpenings } from './openingsGenerator';
import { generateRoof } from './roofGenerator';
import { generateAccessories } from './accessoriesGenerator';
import { planInteriorDoors } from './roomGraph';
import { planStairs } from './stairsPlanner';
import { generateExtras, terraceSlabHolesForStory } from './extrasGenerator';
import { generateBasement } from './basementGenerator';
import { generateGarage } from './garageGenerator';
import { pickTextures } from './texturesGenerator';
import { scoreCandidate, type ScoreBreakdown } from './scorer';

export interface GenerateOptions {
  /** Number of candidates to draw before accepting the best. */
  maxCandidates?: number;
  /** Early-accept threshold — first candidate scoring above this wins. */
  acceptScore?: number;
}

export interface GenerateResult {
  config: HouseConfig;
  score: ScoreBreakdown;
  /** Reasons candidates were rejected. Useful for the debug panel. */
  rejections: Array<{ score: number; reasons: string[] }>;
  candidatesTried: number;
}

/**
 * 12-step pipeline from design doc §"Procedural Generation Strategy".
 *   1. Resolve user constraints.
 *   2. Select / derive style preset.
 *   3. Generate lot dimensions.
 *   4. Generate footprint within setbacks.
 *   5. Determine story count and heights.
 *   6. Partition each story into rooms.
 *   7. Place circulation elements and main entry. (rolled into steps 6/8)
 *   8. Place windows and doors.
 *   9. Generate roof.
 *  10. Generate accessory elements.
 *  11. Score the result.
 *  12. Retry if score below threshold.
 */
export function generateHouse(
  input: GenerationConstraints,
  options: GenerateOptions = {},
): GenerateResult {
  const maxCandidates = options.maxCandidates ?? 6;
  const acceptScore = options.acceptScore ?? 0.82;

  const rejections: GenerateResult['rejections'] = [];
  let best: { cfg: HouseConfig; score: ScoreBreakdown } | null = null;

  for (let i = 0; i < maxCandidates; i++) {
    const rootRng = createRng(`${input.seed}::candidate-${i}`);
    const cfg = buildCandidate(rootRng, input);
    const score = scoreCandidate(cfg);
    cfg.score = score.total;

    if (!best || score.total > best.score.total) {
      best = { cfg, score };
    }
    if (score.total >= acceptScore) break;

    rejections.push({
      score: score.total,
      reasons: score.dimensions
        .filter((d) => d.score < 0.4)
        .map((d) => `${d.name} (${d.score.toFixed(2)})`),
    });
  }

  if (!best) throw new Error('generator produced no candidates');
  return {
    config: best.cfg,
    score: best.score,
    rejections,
    candidatesTried: rejections.length + 1,
  };
}

function buildCandidate(rng: Rng, input: GenerationConstraints): HouseConfig {
  // 1. Resolve.
  const constraints = resolveConstraints(input);

  // 2. Pick preset.
  const preset = pickPreset(rng.fork('preset'), constraints);

  // 3. Lot.
  const lot = generateLot(rng.fork('lot'), preset, constraints);

  // 4. Footprint.
  const body = generateFootprint(rng.fork('footprint'), lot, preset, constraints);

  // 5. Story count.
  const storyCount = pickStoryCount(rng.fork('stories'), preset, constraints);

  // 6 + 8 (interleaved per story because openings depend on rooms).
  const stories: StoryConfig[] = [];
  for (let level = 0; level < storyCount; level++) {
    const storyRng = rng.fork(`rooms-L${level}`);
    const rooms = planStory(storyRng, { level, body, constraints });
    stories.push({
      level,
      elevation: level * body.storyHeight,
      rooms,
      windows: [],
      doors: [],
      partitionDoors: [],
      slabHoles: [],
    });
  }

  // 6b. Interior doorway graph per story — every room must be reachable.
  for (const story of stories) {
    const doorRng = rng.fork(`partition-doors-L${story.level}`);
    story.partitionDoors = planInteriorDoors(doorRng, story.rooms);
  }

  // 6c. Exterior openings (windows + doors) come BEFORE stairs so the
  // stair planner can avoid landing/starting in front of the front door
  // (or any side/rear door) and any partition doorway.
  const openings = generateOpenings(rng.fork('openings'), { body, preset, stories });
  for (let i = 0; i < stories.length; i++) {
    const story = stories[i]!;
    const o = openings[i]!;
    story.windows = o.windows;
    story.doors = o.doors;
  }

  // 6d. Stairs (and matching slab holes) for multi-story houses. The
  // planner now checks every candidate against partitions and door swing
  // zones on both stories, so flights stay inside one room and don't
  // dump people into the entry hall.
  const stairResult = planStairs(
    rng.fork('stairs'),
    body,
    stories.map((s) => ({
      rooms: s.rooms,
      doors: s.doors,
      partitionDoors: s.partitionDoors,
    })),
  );
  for (const story of stories) {
    const holes = stairResult.slabHolesByStory.get(story.level);
    if (holes) story.slabHoles = holes;
  }

  // 9. Roof.
  const roof = generateRoof(rng.fork('roof'), body, preset, constraints);

  // 10. Accessories.
  const { fence, mailbox } = generateAccessories(
    rng.fork('accessories'),
    lot,
    body,
    preset,
    constraints,
  );

  // 10b. Decor toggles + plinth + fence style.
  const decorRng = rng.fork('decor');
  const windowMullion = decorRng.chance(0.45);
  // Plinth is suppressed when a basement is requested: a plinth is a
  // continuous solid slab between grade and the L0 floor, and we'd have
  // to cut a stairwell hole through it AND extend the basement walls up
  // through the lift to keep the well visible. Skipping the plinth in
  // that case leaves the L0 slab the only thing between basement and
  // sky — which IS cut around the stair — so the well actually shows.
  // Without this, the user reports the basement floor "isn't cut" even
  // though the L0 slab itself is — the plinth was hiding it.
  const wantsPlinth = !constraints.basement.value && decorRng.chance(0.55);
  if (wantsPlinth) {
    body.plinthHeight = 0.3 + decorRng.range(0, 0.25);
    body.hasFrontSteps = true;
  }
  if (fence) {
    fence.style = decorRng.weighted(
      ['picket', 'slat', 'rail', 'hedge'] as const,
      [4, 2, 3, 2],
    );
  }

  // 10c. Extras: ground decks (patios) + non-ground balconies. These run
  // AFTER openings so we know where the front door is (we never put a deck
  // on the front facade) and so we can drop any windows that would clash
  // with a deck/balcony's house door.
  const extras = generateExtras(
    rng.fork('extras'),
    body,
    stories,
    stairResult.stairs,
  );
  for (const [level, doors] of extras.extraDoorsByLevel) {
    const story = stories[level];
    if (!story) continue;
    story.doors.push(...doors);
  }
  if (extras.droppedWindowIds.size > 0) {
    for (const story of stories) {
      story.windows = story.windows.filter((w) => !extras.droppedWindowIds.has(w.id));
    }
  }
  for (const [level, balcony] of extras.balconyByLevel) {
    const story = stories[level];
    if (story) story.balcony = balcony;
  }
  // Terrace notches carve every story ABOVE the terrace's deck story:
  // slab holes match the notch AABB so the deck is open to sky (or,
  // for mid-story terraces in a future phase, to the floor below).
  if (extras.terraces.length > 0) {
    for (const story of stories) {
      const extraHoles = terraceSlabHolesForStory(body, extras.terraces, story.level);
      if (extraHoles.length > 0) {
        story.slabHoles = [...story.slabHoles, ...extraHoles];
      }
    }
    // A top-story terrace needs the roof to have a notch cut out of it,
    // and we only know how to cut FLAT roofs today (non-convex gable /
    // hip footprints are deferred — see buildRoof). If the roof
    // generator picked gable or hip, we'd also have to re-use that
    // roof's PEAK HEIGHT as the flat slab thickness, producing the
    // ~3m-tall "black slab" the user reported. Force the roof to a
    // proper flat parapet here so the geometry matches the schema.
    if (roof.type !== 'flat') {
      roof.type = 'flat';
      roof.height = 0.25;
      delete (roof as { direction?: unknown }).direction;
    }
  }

  // 10d. Basement (optional). Punches a stair well into the L0 slab and
  // adds the matching flight. We run this AFTER inter-story stairs are
  // placed so the flights don't fight over the same slab hole.
  let basementCfg: ReturnType<typeof generateBasement> | null = null;
  if (constraints.basement.value && stories[0]) {
    // Every stair that STARTS on L0 (i.e. the L0→L1 flight) takes up
    // floor area on L0, and anyone using it needs approach / landing
    // space at both ends. `stories[0].slabHoles` only covers slab
    // openings, which for L0 is initially empty — the upper-stair
    // hole lives in L1's slabHoles, not L0's. Without this the
    // basement stair can land right next to the upper stair, leaving
    // no walkable floor between the two (user-reported, see image).
    //
    // We forbid the upper stair's footprint padded by
    // STAIR_FOOTPRINT_PAD on all four sides. That reserves a ring of
    // floor around the flight that the basement stair can't touch.
    const STAIR_FOOTPRINT_PAD = 0.6;
    const l0Forbidden = [
      ...stories[0].slabHoles.map((h) => ({
        xLo: h.x,
        xHi: h.x + h.width,
        zLo: h.z,
        zHi: h.z + h.depth,
      })),
      ...stairResult.stairs
        .filter((s) => s.toLevel === 1)
        .map((s) => ({
          xLo: s.x - STAIR_FOOTPRINT_PAD,
          xHi: s.x + s.width + STAIR_FOOTPRINT_PAD,
          zLo: s.z - STAIR_FOOTPRINT_PAD,
          zHi: s.z + s.depth + STAIR_FOOTPRINT_PAD,
        })),
    ];
    basementCfg = generateBasement(
      rng.fork('basement'),
      body,
      lot,
      {
        rooms: stories[0].rooms,
        doors: stories[0].doors,
        partitionDoors: stories[0].partitionDoors,
      },
      l0Forbidden,
    );
    if (basementCfg) {
      stories[0].slabHoles = [...stories[0].slabHoles, basementCfg.groundSlabHole];
    }
  }

  // 10e. Attached garage (optional). Adds a service door to story 0 and
  // drops any windows that would overlap it. The garage itself is a
  // sibling of the house body rather than a story (see buildGarage).
  let garageCfg: ReturnType<typeof generateGarage> | null = null;
  if (constraints.garage.value) {
    garageCfg = generateGarage(rng.fork('garage'), body, lot, stories[0]!);
    if (garageCfg && stories[0]) {
      if (garageCfg.garage.hasInteriorDoor) {
        stories[0].doors.push(garageCfg.interiorDoor);
        const doorLo = garageCfg.interiorDoor.offset - 0.2;
        const doorHi =
          garageCfg.interiorDoor.offset + garageCfg.interiorDoor.width + 0.2;
        stories[0].windows = stories[0].windows.filter(
          (w) =>
            w.side !== garageCfg!.interiorDoor.side ||
            w.offset + w.width <= doorLo ||
            w.offset >= doorHi,
        );
      }
      // Also drop any windows that would fall WITHIN the shared-wall
      // span — the garage is on the other side of that wall, so a
      // window there would look into the garage interior.
      const shared = garageCfg.garage;
      const shLo = shared.offset - 0.1;
      const shHi = shared.offset + shared.width + 0.1;
      stories[0].windows = stories[0].windows.filter(
        (w) =>
          w.side !== shared.side ||
          w.offset + w.width <= shLo ||
          w.offset >= shHi,
      );
    }
  }

  const textures = pickTextures(rng.fork('textures'));

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    kind: 'house',
    seed: input.seed,
    score: 0,
    lot,
    house: body,
    stories,
    roof,
    stairs: stairResult.stairs,
    ...(basementCfg ? { basement: basementCfg.basement } : {}),
    ...(garageCfg ? { garage: garageCfg.garage } : {}),
    ...(garageCfg?.driveway ? { driveway: garageCfg.driveway } : {}),
    decks: extras.decks,
    terraces: extras.terraces,
    ...(fence ? { fence } : {}),
    ...(mailbox ? { mailbox } : {}),
    palette: preset.palette,
    style: {
      preset: preset.id,
      trimAccent: true,
      symmetricFront: constraints.symmetricFront.value,
      windowMullion,
    },
    ...(textures ? { textures } : {}),
  };
}

// For discrete categorical choices (preset, story count, roof type) we
// always honor the user's current value whether or not the lock is on.
// The lock toggle only affects numeric ranges (lot width/depth) where
// "soft" still means "the generator may clamp to a feasible range". A
// discrete pick has nothing to relax — if the user clicked "Modern" they
// expect Modern, and Re-roll should produce a different Modern house, not
// randomise the whole archetype.
//
// The `rng` argument is kept for API symmetry and future soft-weighting.
function pickPreset(
  _rng: Rng,
  constraints: ReturnType<typeof resolveConstraints>,
): StylePresetSpec {
  return STYLE_PRESETS[constraints.stylePreset.value];
}

function pickStoryCount(
  _rng: Rng,
  _preset: StylePresetSpec,
  constraints: ReturnType<typeof resolveConstraints>,
): number {
  return Math.max(1, Math.min(3, Math.round(constraints.storyCount.value)));
}
